import React, { useMemo } from 'react';
import { Typography } from '@repo/ui';
import { groupAndSortSubmetrics } from '@repo/utils';
import { InsightMetric } from 'types/Insights';
import useSubMetricEvaluations from 'services/insights/useSubMetricEvaluations';

import styles from './CategoryHealthBars.module.scss';

const evaluationColorMap: Record<string, string> = {
  red: 'needs_support',
  orange: 'needs_improvement',
  grey: 'okay_results',
  green: 'great_results',
};

const colorClassMap: Record<string, string> = {
  needs_support: styles.red,
  needs_improvement: styles.orange,
  okay_results: styles.grey,
  great_results: styles.green,
};

interface BarSegment {
  percentage: number;
  className: string;
}

interface CategoryHealthData {
  categoryName: string;
  segments: BarSegment[];
}

export const CategoryHealthBars: React.FC = () => {
  const { data: submetrics } = useSubMetricEvaluations();

  const categoryHealthData = useMemo<CategoryHealthData[]>(() => {
    const groupedSubmetrics = groupAndSortSubmetrics(submetrics);

    return groupedSubmetrics.map(category => {
      const categorySubmetrics: InsightMetric[] = [];

      category.orderedMetrics.forEach(metric => {
        categorySubmetrics.push(...metric.orderedSubmetrics);
      });

      const evaluationCounts: Record<string, number> = {
        needs_support: 0,
        needs_improvement: 0,
        okay_results: 0,
        great_results: 0,
      };

      categorySubmetrics.forEach(submetric => {
        const evaluationName =
          evaluationColorMap[submetric.evaluation_color ?? ''];
        if (evaluationName) {
          evaluationCounts[evaluationName]++;
        }
      });

      const total = categorySubmetrics.length;
      const segments = Object.entries(evaluationCounts)
        .filter(([_, count]) => count > 0)
        .map(([evaluation, count]) => ({
          percentage: (count / total) * 100,
          className: colorClassMap[evaluation],
        }))
        .sort((a, b) => {
          const order = [
            'great_results',
            'okay_results',
            'needs_improvement',
            'needs_support',
          ];
          const aEval = Object.keys(colorClassMap).find(
            key => colorClassMap[key] === a.className,
          );
          const bEval = Object.keys(colorClassMap).find(
            key => colorClassMap[key] === b.className,
          );
          return order.indexOf(aEval || '') - order.indexOf(bEval || '');
        });

      return {
        categoryName: category.name,
        segments,
      };
    });
  }, [submetrics]);

  return (
    <div className={styles.container}>
      {categoryHealthData.map((category, index) => (
        <div key={category.categoryName} className={styles.categoryItem}>
          <Typography variant='body-s'>{category.categoryName}</Typography>
          <div
            className={styles.barContainer}
            style={{
              animationDelay: `${index * 150}ms`,
            }}
          >
            {category.segments.map((segment, segmentIndex) => (
              <div
                key={segmentIndex}
                className={segment.className}
                style={{
                  width: `${segment.percentage}%`,
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
